import { Base } from '@studiometa/js-toolkit';

const KLAVIYO_API_REVISION = '2026-01-15';

/**
 * Contest Form — inscription à un jeu concours.
 *
 * Flux soumission :
 *   1. POST /client/profiles — crée/met à jour le profil avec nom, prénom et les
 *      propriétés du jeu concours (nom du concours, date, acceptation du règlement).
 *      Fait pour tous les participants : l'inscription au jeu n'est pas un
 *      consentement marketing.
 *   2. POST /client/subscriptions — uniquement si la case facultative est cochée,
 *      enregistre le consentement marketing sur la liste configurée dans la section.
 * @see shops/core/sections/section-contest-form.liquid
 */
export default class ContestForm extends Base {
  static config = {
    name: 'ContestForm',
    refs: [
      'form',
      'firstName',
      'lastName',
      'email',
      'rules',
      'optin',
      'submit',
      'submitText',
      'error',
      'success',
    ],
    options: {
      contestName: String,
      listId: String,
    },
  };

  /**
   * Soumission du formulaire.
   * @param {object} props
   * @param {SubmitEvent} props.event
   */
  async onFormSubmit({ event }) {
    event.preventDefault();

    // Validation native : champs requis + format de l'email + case règlement
    if (!this.$refs.form.reportValidity()) {
      return;
    }

    const email = this.$refs.email.value.trim();
    const firstName = this.$refs.firstName.value.trim();
    const lastName = this.$refs.lastName.value.trim();
    const optin = this.$refs.optin.checked;

    this.setLoading(true);
    this.$refs.error.classList.add('hidden');

    try {
      await this.createProfile({ email, firstName, lastName });

      // L'abonnement marketing n'est fait qu'avec le consentement explicite
      if (optin && this.$options.listId) {
        await this.subscribe({ email, firstName, lastName });
      }

      this.$refs.form.classList.add('hidden');
      this.$refs.success.classList.remove('hidden');
    } catch (err) {
      console.error('[ContestForm]', err);
      this.$refs.error.classList.remove('hidden');
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Active/désactive l'état de chargement sur le bouton submit.
   * @param {boolean} isLoading
   */
  setLoading(isLoading) {
    this.$refs.submit.disabled = isLoading;
    this.$refs.submitText.classList.toggle('invisible', isLoading);
    const spinner = this.$refs.submit.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }

  /**
   * Appel 1 — Crée ou met à jour le profil Klaviyo du participant.
   * Les propriétés permettent de segmenter les participants du jeu concours
   * sans les abonner à la newsletter.
   * @param {object} props
   * @param {string} props.email
   * @param {string} props.firstName
   * @param {string} props.lastName
   */
  async createProfile({ email, firstName, lastName }) {
    const language = (window.Shopify?.locale ?? 'en').split('-')[0];

    const res = await fetch(
      `https://a.klaviyo.com/client/profiles/?company_id=${window.klaviyoPublicKey}`,
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          revision: KLAVIYO_API_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: 'profile',
            attributes: {
              email,
              first_name: firstName,
              last_name: lastName,
              properties: {
                language,
                contest: this.$options.contestName,
                contest_rules_accepted: true,
                contest_signup_at: new Date().toISOString(),
              },
            },
          },
        }),
      },
    );

    // 409 Conflict = profil déjà existant, Klaviyo fusionne les données → OK
    if (!res.ok && res.status !== 409) {
      throw new Error(`[Klaviyo Profile] HTTP ${res.status}`);
    }
  }

  /**
   * Appel 2 — Enregistre le consentement marketing sur la liste configurée.
   * @param {object} props
   * @param {string} props.email
   * @param {string} props.firstName
   * @param {string} props.lastName
   */
  async subscribe({ email, firstName, lastName }) {
    const res = await fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${window.klaviyoPublicKey}`,
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          revision: KLAVIYO_API_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: this.$options.contestName,
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                    first_name: firstName,
                    last_name: lastName,
                    subscriptions: {
                      email: { marketing: { consent: 'SUBSCRIBED' } },
                    },
                  },
                },
              },
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: this.$options.listId,
                },
              },
            },
          },
        }),
      },
    );

    if (!res.ok) {
      throw new Error(`[Klaviyo Subscription] HTTP ${res.status}`);
    }
  }
}
